import { Link } from 'react-router';
import { Juridisk } from '../components/Juridisk';
import { SELSKAP } from '../lib/selskap';
import { SIDETITLER, useSidehode } from '../lib/sidehode';
import { SVARLENKE_DAGER } from '../lib/svarlenke';

/**
 * Bruksvilkår.
 *
 * Avtalen om å *bruke* tjenesten. Kjøpet er et annet dokument
 * (`SalgsbetingelserPage`), fordi det er to forskjellige ting: bruksvilkårene
 * gjelder alle med konto, også de som aldri betaler, mens salgsbetingelsene
 * bare gjelder den som kjøper en pakke.
 *
 * Teksten beskriver det koden faktisk gjør i dag. Det som ikke er bygget, står
 * ikke som et løfte — samme regel som personvernerklæringen følger, og grunnen
 * er den samme: et vilkår man ikke holder, er verre enn intet vilkår.
 */
export function VilkaarPage() {
  useSidehode(SIDETITLER.vilkaar, '/vilkaar');
  return (
    <Juridisk
      tittel="Bruksvilkår"
      ingress={
        <p>
          Disse vilkårene gjelder mellom deg og {SELSKAP.navn} når du bruker {SELSKAP.tjeneste}. Du
          godtar dem når du oppretter konto. Kjøper du en pakke, gjelder i tillegg{' '}
          <Link to="/salgsbetingelser">salgsbetingelsene</Link>, og{' '}
          <Link to="/personvern">personvernerklæringen</Link> forklarer hvordan opplysninger
          behandles.
        </p>
      }
    >
      <h2>1. Hva tjenesten er</h2>
      <p>
        {SELSKAP.tjeneste} er et verktøy for å planlegge et bryllup: gjesteliste med digital RSVP,
        budsjett, oppgaver, dagsprogram, ønskeliste, bordplassering og utsending av e-post til
        gjestene. Hvilke deler dere har tilgang til, avhenger av pakken.
      </p>
      <p>
        Vi er et verktøy, ikke en arrangør. Vi inngår ingen avtaler med lokaler, fotografer eller
        andre leverandører på deres vegne, og har ikke ansvar for avtaler dere gjør med dem.
      </p>

      <h2>2. Konto</h2>
      <ul>
        <li>Du må være fylt 18 år for å opprette konto.</li>
        <li>
          Opplysningene du registrerer, skal være riktige. E-postadressen brukes til innlogging,
          til å tilbakestille passord og til tjenestemeldinger.
        </li>
        <li>
          Passordet er ditt ansvar. Del det ikke. Er du redd noen andre har fått tilgang, bytt
          passord — da logges alle andre økter ut.
        </li>
        <li>
          Én konto tilhører én person. Skal flere planlegge sammen, inviterer dere dem inn som
          partner, toastmaster eller forlover, slik at hver av dem har sin egen innlogging.
        </li>
      </ul>

      <h2>3. Hva dere legger inn</h2>
      <p>
        Dere bestemmer selv hva som legges inn i tjenesten, og dere er ansvarlige for det. Dere må
        ikke legge inn innhold som er ulovlig, krenkende, eller som dere ikke har rett til å bruke.
      </p>
      <p>
        Vi går ikke gjennom innholdet deres. Blir vi gjort oppmerksomme på innhold som klart bryter
        med dette punktet eller med norsk lov, kan vi fjerne det og i grove tilfeller stenge
        kontoen. Da sier vi fra om hvorfor, og dere får hentet ut det dere har lagt inn, med mindre
        loven er til hinder for det.
      </p>

      <h2>4. Opplysninger om gjestene</h2>
      <p>
        Dette punktet er viktigere enn det ser ut som. Når dere legger inn navn, e-post, telefon,
        allergier og notater om gjestene deres, er det <strong>vi</strong> som er{' '}
        <em>behandlingsansvarlige</em> for de opplysningene. Det er vi som har bestemt hvilke felt
        som finnes, hvordan svarlenken virker, hvordan opplysningene krypteres og når de slettes —
        og da er det vi som svarer for dem.{' '}
        <Link to="/personvern">Personvernerklæringen</Link> forklarer hva vi lagrer, hva
        behandlingen hviler på, og hvor lenge den varer.
      </p>
      <p>
        Dere bestemmer fortsatt hvem som skal stå på lista, og hva som skal noteres om dem. Med det
        følger noen plikter:
      </p>
      <ul>
        <li>
          <strong>Legg inn det som trengs, ikke mer.</strong> Opplysningene skal være riktige og
          relevante for å planlegge selskapet, og dere skal ha fått dem på ærlig vis — fordi gjesten
          har oppgitt dem, eller fordi dere kjenner dem.
        </li>
        <li>
          <strong>Allergier og kosthold kan si noe om helse.</strong> Legg dem inn fordi gjesten har
          fortalt dere om dem, ikke fordi dere gjetter, og bekreft i skjemaet at dere har spurt.
          Gjesten kan også skrive dem selv på svarlenken sin.
        </li>
        <li>
          <strong>Resten hører ikke hjemme her.</strong> Notatfeltet er til praktiske ting — hvem som
          kjører hjem, hvem som ikke bør sitte ved siden av hverandre. Det er ikke stedet for helse,
          religion, legning eller politikk ut over det tjenesten uttrykkelig spør om.
        </li>
        <li>
          <strong>Spør en gjest hva som er registrert om dem,</strong> eller ber om å bli rettet
          eller slettet, kan de komme til oss på{' '}
          <a href={`mailto:${SELSKAP.epost}`}>{SELSKAP.epost}</a> — eller til dere. Kommer de
          til dere, retter eller fjerner dere gjesten i gjestelista. Kommer de til oss, tar vi det,
          og sier fra til dere hvis noen forsvinner fra lista.
        </li>
      </ul>
      <p>
        Teksten på <Link to="/gjesteinformasjon">vardag.no/gjesteinformasjon</Link> forklarer dette
        for gjestene, og er lenket fra svarsiden deres.
      </p>

      <h2>5. Svarlenkene til gjestene</h2>
      <p>
        Hver gjest får sin egen svarlenke. Den er en lang, tilfeldig adresse og krever ingen
        innlogging — det er et bevisst valg, fordi et bryllup ikke er et sted å kreve at åtti
        mennesker lager konto. Lenken bør derfor behandles som en nøkkel: den som har den, kan svare
        på vegne av gjesten og se det gjesten skal se.
      </p>
      <p>
        Nøkkelen har to sperrer. Den <strong>slutter å virke {SVARLENKE_DAGER} dager etter
        bryllupsdagen</strong>, og det gjesten har skrevet om mat, <strong>vises ikke tilbake</strong>{' '}
        på lenken. Er en lenke kommet på avveie før den tid, kan dere lage en ny til den gjesten
        fra gjestelista — den gamle slutter å virke med det samme, også om den alt er sendt.
      </p>

      <h2>6. Roller og deling</h2>
      <p>
        Dere kan gi partneren, toastmasteren og forloveren tilgang, og bestemmer selv hva hver rolle
        får se og endre. Gir dere noen tilgang til gjestelista, får de se opplysningene som ligger
        der, allergier medregnet. Tilgangen kan endres eller trekkes tilbake når som helst, og
        gjelder fra det øyeblikket dere lagrer.
      </p>

      <h2>7. E-post til gjestene</h2>
      <p>
        Meldinger dere sender fra tjenesten, går ut fra vår avsenderadresse, men innholdet er deres
        og svar går til dere. Dere er ansvarlige for det dere skriver, og for at mottakerne faktisk
        er gjester dere har en grunn til å kontakte. Tjenesten skal ikke brukes til markedsføring
        eller til å sende e-post til andre enn deres egne gjester.
      </p>

      <h2>8. Tilgjengelighet</h2>
      <p>
        Vi gjør vårt beste for at tjenesten skal være tilgjengelig hele tiden, men garanterer ikke
        drift uten avbrudd. Vedlikehold legges så langt det lar seg gjøre til tider med lite bruk.
        Ved lengre planlagte avbrudd varsler vi på forhånd.
      </p>
      <p>
        Skulle tjenesten legges ned, varsler vi minst tre måneder i forveien, slik at dere rekker å
        laste ned det dere har lagt inn.
      </p>

      <h2>9. Endringer i tjenesten og i vilkårene</h2>
      <p>
        Tjenesten utvikles løpende. Vi kan legge til, endre og fjerne funksjoner. Fjerner vi noe som
        var en vesentlig del av pakken dere har betalt for, varsler vi på e-post, og dere kan da si
        opp og få tilbake en forholdsmessig del av det dere har betalt.
      </p>
      <p>
        Vilkårene kan endres. Endringer varsles på e-post minst 30 dager før de trer i kraft. Er du
        uenig i endringen, kan du si opp avtalen og slette kontoen før den trer i kraft.
      </p>

      <h2>10. Rettigheter</h2>
      <p>
        Tjenesten, koden, utformingen og navnet {SELSKAP.tjeneste} tilhører {SELSKAP.navn}. Dere får
        en ikke-eksklusiv rett til å bruke tjenesten så lenge avtalen løper.
      </p>
      <p>
        Innholdet dere legger inn, er deres. Vi bruker det bare til å levere tjenesten til dere, og
        gjør ingenting annet med det.
      </p>

      <h2>11. Ansvar</h2>
      <p>
        Tjenesten er et hjelpemiddel til å holde oversikt. Dere er selv ansvarlige for beslutningene
        dere tar under planleggingen, og for å kontrollere at det som står i tjenesten, stemmer.
      </p>
      <p>
        Vi er ansvarlige etter alminnelige regler for tap som skyldes feil hos oss. Vi svarer ikke
        for indirekte tap, og ansvaret er uansett begrenset til det dere har betalt for tjenesten —
        med mindre vi har opptrådt grovt uaktsomt eller forsettlig. Ingenting her begrenser rettigheter
        du har som forbruker etter ufravikelig norsk lov.
      </p>

      <h2>12. Oppsigelse</h2>
      <p>
        Du kan når som helst slette kontoen din under Konto og personvern. Da slettes opplysningene
        om deg, bryllupet og gjestene. Betalte beløp refunderes ikke ved sletting, ut over det som
        følger av <Link to="/salgsbetingelser">angreretten</Link>.
      </p>
      <p>
        Vi kan si opp avtalen med 30 dagers varsel, eller med umiddelbar virkning ved vesentlig
        mislighold — for eksempel bruk som er ulovlig eller som rammer andre brukere.
      </p>

      <h2>13. Lovvalg og tvister</h2>
      <p>
        Avtalen reguleres av norsk rett. Er du forbruker, kan du alltid gå til domstolen på ditt
        eget hjemsted. Vi prøver først å løse uenigheter direkte — ta kontakt på{' '}
        <a href={`mailto:${SELSKAP.epost}`}>{SELSKAP.epost}</a>. Fører det ikke fram, kan saken
        bringes inn for Forbrukertilsynet, se{' '}
        <a href="https://www.forbrukertilsynet.no" target="_blank" rel="noreferrer">
          forbrukertilsynet.no
        </a>
        .
      </p>
    </Juridisk>
  );
}
